import { z } from "zod";
import { PackageType } from "../contractorPackage";
import { collaboratorDetailSchema } from "../organisation";
export var ReportCadence;
(function (ReportCadence) {
    ReportCadence["CurrentMonth"] = "report-cadence_current-month";
    ReportCadence["FutureMonth"] = "report-cadence_future-month";
})(ReportCadence || (ReportCadence = {}));
export const reportCadenceToString = (cadence) => {
    switch (cadence) {
        case ReportCadence.CurrentMonth:
            return "Current Month";
        case ReportCadence.FutureMonth:
            return "Future Month";
    }
};
export var ProjectSector;
(function (ProjectSector) {
    ProjectSector["Building"] = "project-sector_building";
    ProjectSector["Infrastructure"] = "project-sector_infrastructure";
    ProjectSector["RailAndTransport"] = "project-sector_rail-and-transport";
    ProjectSector["RenewablesAndEnergy"] = "project-sector_renewables-and-energy";
})(ProjectSector || (ProjectSector = {}));
export const projectSectorToString = (sector) => {
    switch (sector) {
        case ProjectSector.Building:
            return "Building";
        case ProjectSector.Infrastructure:
            return "Infrastructure";
        case ProjectSector.RailAndTransport:
            return "Rail and Transport";
        case ProjectSector.RenewablesAndEnergy:
            return "Renewables and Energy";
    }
};
export var ProjectAccessLevel;
(function (ProjectAccessLevel) {
    ProjectAccessLevel["Admin"] = "project-access-level_admin";
    ProjectAccessLevel["Level1Only"] = "project-access-level_level-1-only";
    ProjectAccessLevel["Manager"] = "project-access-level_manager";
    ProjectAccessLevel["Write"] = "project-access-level_write-only";
    ProjectAccessLevel["ReadOnly"] = "project-access-level_read-only";
    ProjectAccessLevel["NoAccess"] = "project-access-level_no-access";
})(ProjectAccessLevel || (ProjectAccessLevel = {}));
export const projectAccessSchema = z.object({
    contractor_package_id: z.string(),
    project_access_level: z.enum(ProjectAccessLevel),
    project_id: z.string()
});
const accessLevelPriority = {
    [ProjectAccessLevel.Admin]: 5,
    [ProjectAccessLevel.Level1Only]: 4,
    [ProjectAccessLevel.Manager]: 3,
    [ProjectAccessLevel.NoAccess]: 0,
    [ProjectAccessLevel.ReadOnly]: 1,
    [ProjectAccessLevel.Write]: 2
};
export const getDistinctProjectAccess = (projectAccessInfo) => {
    // Map to store the highest access level for each project_id
    const projectAccessMap = new Map();
    for (const pai of projectAccessInfo) {
        const { project_access_level, project_id } = pai;
        const currentLevel = projectAccessMap.get(project_id);
        // Update if no access level exists or if the new level has higher priority
        if (!currentLevel ||
            accessLevelPriority[project_access_level] >
                accessLevelPriority[currentLevel.project_access_level]) {
            projectAccessMap.set(project_id, pai);
        }
    }
    // Convert map to array of ProjectProfileAccess objects
    return Array.from(projectAccessMap.entries()).map(([project_id, project_access]) => ({
        contractor_package_id: project_access.contractor_package_id,
        project_access_level: project_access.project_access_level,
        project_id
    }));
};
export const projectAccessLevelToString = (projectAccessLevel) => {
    switch (projectAccessLevel) {
        case ProjectAccessLevel.Admin:
            return "Project Admin";
        case ProjectAccessLevel.Level1Only:
            return "View Only (Level 1)";
        case ProjectAccessLevel.Manager:
            return "Manager";
        case ProjectAccessLevel.Write:
            return "Write";
        case ProjectAccessLevel.ReadOnly:
            return "Read Only";
        default:
        case ProjectAccessLevel.NoAccess:
            return "No Access";
    }
};
export const packageAcceptRequestSchema = z.object({
    contractorPackageId: z.string(),
    projectId: z.string()
});
export const projectSchema = z.object({
    archived: z.boolean(),
    /** Canonical (suffix-stripped) form of organisation_name, derived by the
     * database. Optional: absent on payloads predating the derived column. */
    canonical_name: z.string().nullish(),
    closed_date: z.string().optional(),
    collaborators: z.array(z.string()).optional(),
    commitments_environmental: z.array(z.custom()),
    commitments_hours: z.array(z.custom()),
    commitments_spend: z.array(z.custom()),
    created: z.date(),
    emission_factors: z.array(z.custom()).optional(),
    /** Absent for projects that never turned environmental on. */
    estimated_progress: z.number(),
    financial: z.custom(),
    financial_id: z.string(),
    hammertech_project_id: z.string().optional(),
    is_closed: z.boolean(),
    is_shared: z.boolean().optional(),
    local_content: z.array(z.custom()),
    organisation_id: z.string(),
    organisation_logo: z.string(),
    organisation_name: z.string(),
    policies: z.array(z.custom()),
    project_access_level: z.enum(ProjectAccessLevel),
    project_address: z.custom(),
    project_contact_info_id: z.string(),
    /** YYYY-MM-DD; canonical project forecast/profile end */
    project_end_date: z.string(),
    project_frameworks: z.array(z.custom()).optional(),
    project_id: z.string(),
    project_name: z.string(),
    project_start_date: z.string(),
    report_cadence: z.enum(ReportCadence),
    report_exists: z.boolean().optional(),
    reporting_due_date: z.number(),
    sector: z.enum(ProjectSector),
    targets_environmental: z
        .array(z.custom())
        .optional(),
    user_package_id: z.string(),
    /** Hours a 100% workforce-profile month means on this project. Absent on
     * payloads predating the column; readers fall back to the default. */
    workforce_profile_full_time_monthly_hours: z.number().nullish()
});
export const projectDetailSchema = z.object({
    archived: z.boolean(),
    /** Canonical (suffix-stripped) form of organisation_name, derived by the
     * database. Optional: absent on payloads predating the derived column. */
    canonical_name: z.string().nullish(),
    closed_date: z.string().optional(),
    /** Named twin of `collaborators` — optional so older payloads still decode. */
    collaborator_details: z.array(collaboratorDetailSchema).optional(),
    /** @deprecated Superseded by `collaborator_details`. Logo S3 keys only. */
    collaborators: z.array(z.string()).optional(),
    contractor_package_id: z.string(),
    created: z.date(),
    estimated_progress: z.number(),
    is_accepted: z.boolean(),
    is_closed: z.boolean(),
    organisation_id: z.string(),
    organisation_logo: z.string(),
    organisation_name: z.string(),
    package_description: z.string().optional(),
    package_type: z.enum(PackageType),
    project_access_level: z.enum(ProjectAccessLevel),
    project_address: z.custom(),
    project_contact_info_id: z.string(),
    project_end_date: z.string(),
    project_id: z.string(),
    project_name: z.string(),
    project_start_date: z.string(),
    reporting_due_date: z.number(),
    sector: z.enum(ProjectSector),
    /** Hours a 100% workforce-profile month means on this project. Absent on
     * payloads predating the column; readers fall back to the default. */
    workforce_profile_full_time_monthly_hours: z.number().nullish()
});
